import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { ChevronRight, Loader2 } from "lucide-react";
import CarCard from "../components/CarCard";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi from "../services/odooApi";

export const CategoryPage = () => {
  const { slug } = useParams();
  const { t, tf } = useLanguage();
  const [category, setCategory] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [catData, catCars] = await Promise.all([
          odooApi.getCategory(slug),
          odooApi.getCategoryCars(slug),
        ]);
        setCategory(catData);
        setCars(catCars);
      } catch (err) {
        console.error("Failed to load category:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t("categoryPage.notFound", "Category Not Found")}</h2>
          <Link to="/cars" className="text-[#D4AF37] hover:underline">{t("carDetail.browseAll", "Browse all cars")}</Link>
        </div>
      </div>
    );
  }

  const hasCategoryImage = Boolean(category.image);

  return (
    <>
      <SEO
        title={`${category.name} Car Rental in Dubai & UAE`}
        description={`Rent ${category.name.toLowerCase()} cars in Dubai & UAE. ${category.carCount}+ vehicles. Best prices guaranteed. Browse and book instantly with Velaris Royal.`}
        keywords={`${category.name.toLowerCase()} rental dubai, ${category.name.toLowerCase()} car hire uae, rent ${category.name.toLowerCase()} car, ${category.name.toLowerCase()} for rent`}
        image={category.image}
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("nav.categories", "Categories"), url: "/cars" },
          { name: category.name },
        ]}
      />

      <div className="min-h-screen bg-white dark:bg-gray-950" data-testid="category-page">
        <div className="relative h-[40vh] min-h-[300px] bg-gradient-to-br from-black via-neutral-900 to-[#6b5214]">
          {hasCategoryImage ? (
            <img src={category.image} alt={category.name} className="absolute inset-0 w-full h-full object-cover opacity-60" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-12">
            <nav className="flex items-center gap-2 text-sm mb-4">
              <Link to="/" className="text-white/60 hover:text-[#D4AF37]">{t("nav.home", "Home")}</Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <Link to="/cars" className="text-white/60 hover:text-[#D4AF37]">{t("nav.categories", "Categories")}</Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <span className="text-white">{category.name}</span>
            </nav>
            <h1 className="font-[var(--font-display)] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {tf("categoryPage.rent", { name: category.name }, `Rent ${category.name}`)}
            </h1>
            <p className="text-white/70 max-w-2xl">{category.description}</p>
          </div>
        </div>

        <div className="bg-black border-t border-[#D4AF37]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">{category.carCount || cars.length}</p>
                <p className="text-white/60 text-sm">{t("common.vehicles", "Vehicles")}</p>
              </div>
              <div className="w-px h-12 bg-[#D4AF37]/30" />
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">50+</p>
                <p className="text-white/60 text-sm">{t("common.suppliers", "Suppliers")}</p>
              </div>
              <div className="w-px h-12 bg-[#D4AF37]/30" />
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">7</p>
                <p className="text-white/60 text-sm">{t("common.cities", "Cities")}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 dark:text-white">
              {tf("categoryPage.available", { name: category.name }, `Available ${category.name}`)}
            </h2>
            <Link to={`/cars?category=${encodeURIComponent(category.slug || slug)}`} className="text-[#D4AF37] hover:text-[#C5A028] font-medium flex items-center gap-1">
              {t("btn.viewAll", "View All")}<ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {cars.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="category-cars-grid">
              {cars.map((car) => (<CarCard key={car.id} car={car} />))}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <p className="text-lg">{tf("categoryPage.empty", { category: category.name }, `No ${category.name} available at the moment. Check back soon!`)}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CategoryPage;

